import { Link } from "react-router-dom";
import { collectionsData } from "@/mocks/collections";

interface RelatedPiecesProps {
  currentSlug: string;
  category: string;
}

export default function RelatedPieces({ currentSlug, category }: RelatedPiecesProps) {
  const related = collectionsData
    .filter((item) => item.category === category && item.slug !== currentSlug)
    .slice(0, 3);

  if (related.length === 0) {
    // Fallback: show any other products if same category has none
    const fallback = collectionsData
      .filter((item) => item.slug !== currentSlug)
      .slice(0, 3);
    if (fallback.length === 0) return null;
  }

  const itemsToShow = related.length > 0 ? related : collectionsData.filter((item) => item.slug !== currentSlug).slice(0, 3);

  return (
    <section className="bg-brand-cream py-20 px-6">
      <div className="max-w-7xl mx-auto">
        {/* Section Header */}
        <div className="text-center mb-14">
          <span className="font-display text-brand-gold text-xs tracking-[4px] uppercase block mb-3">
            Curated For You
          </span>
          <h2 className="font-serif text-3xl md:text-4xl text-brand-darkbrown font-bold">
            You May Also <em>Like</em>
          </h2>
        </div>

        {/* Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
          {itemsToShow.map((item) => (
            <Link
              key={item.id}
              to={`/product/${item.slug}`}
              className="group bg-brand-beige border border-brand-gold/10 rounded-lg overflow-hidden hover:border-brand-gold/35 transition-all duration-400 cursor-pointer block"
              data-product-shop
            >
              {/* Image */}
              <div className="w-full h-64 overflow-hidden relative">
                <img
                  src={item.image}
                  alt={item.name}
                  className="w-full h-full object-cover object-top transition-transform duration-700 group-hover:scale-105"
                />
                <div className="absolute inset-0 bg-brand-darkbrown/0 group-hover:bg-brand-darkbrown/20 transition-all duration-400" />
                <div className="absolute top-4 left-4 bg-brand-gold text-brand-darkbrown font-display text-[10px] tracking-widest uppercase px-3 py-1 rounded-full">
                  {item.category}
                </div>
                <div className="absolute top-4 right-4 bg-brand-darkbrown/50 backdrop-blur-sm text-brand-cream font-display text-[10px] tracking-widest uppercase px-3 py-1 rounded-full border border-brand-gold/30">
                  Handcrafted
                </div>
              </div>

              {/* Content */}
              <div className="p-7">
                <h3 className="font-serif text-xl text-brand-darkbrown font-bold mb-1">
                  {item.name}
                </h3>
                <p className="font-display text-sm text-brand-gold font-semibold mb-3">
                  Visit Our Showroom
                </p>
                <p className="font-sans text-sm text-brand-muted leading-relaxed mb-4">
                  {item.description}
                </p>
                <span className="whitespace-nowrap inline-flex items-center justify-center gap-2 font-display text-xs tracking-widest uppercase px-6 py-2.5 bg-transparent border-[1.5px] border-brand-gold text-brand-gold rounded-full hover:bg-brand-gold hover:text-brand-darkbrown transition-all duration-300 cursor-pointer w-full">
                  View Details
                  <i className="ri-arrow-right-line" />
                </span>
              </div>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
}
